import React, { useEffect, useState } from "react";
import axios from "axios";

import { Pagination, Stack } from "@mui/material";
import { Loader } from "./loader";
import moment from "moment";
import { Modal } from "react-bootstrap";
import { EditUserData } from "./adminComp/userEditModal";
import UserPDF from "./adminComp/userConsentPDF";
import api from "../../api";
import "./UsersConsent.css";

// Lucide Icons
import {
  Search,
  RotateCcw,
  Download,
  User,
  Mail,
  FileText,
  Signature,
  Globe,
  Calendar,
  Filter,
  ChevronRight,
  ChevronLeft,
  Plus,
} from "lucide-react";
import { useAlert } from "react-alert";

function UsersConsent({ adminType }) {
  const alert = useAlert();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState([]);
  const [noOfUser, setnoOfUser] = useState(0);
  const [loader, setLoader] = useState(adminType !== "sackAdmin755");
  const [searchMail, setSearchMail] = useState("");
  const [resetPage, setResetPage] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [showEdit, setShowEdit] = useState(false);
  const handleCloseEdit = () => setShowEdit(false);

  const getUsers = async () => {
    try {
      let payload = {
        page: page,
      };
      setLoader(true);
      const userData = await api.getConsentsUser(payload);
      setData(userData.user);
      setnoOfUser(userData.count);
      setLimit(userData.limit);
      setLoader(false);
    } catch (error) {
      console.log(error?.message);
      setLoader(false);
    }
  };

  const handleSearch = async () => {
    if (searchMail) {
      let payload = {
        email: searchMail,
      };
      const res = await api.searchConsentsUser(payload);
      if (res?.success) {
        setData(res?.user);
      } else {
        alert.info("Email address not found");
      }
    } else {
      alert.info("Please insert email for search");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  useEffect(() => {
    if (adminType !== "sackAdmin755") {
      getUsers();
    }
  }, [page, resetPage]);

  const handleReset = () => {
    setResetPage(!resetPage);
    setPage(1);
    setSearchMail("");
  };

  return (
    <>
      <div className="users-consent-container">
        {/* Header exactly matching the image */}
        <div className="consent-header-section">
          <div className="consents-header-content">
            <div className="consent-header-title-wrapper">
              <h1 className="header-title">User Consents</h1>
              <span className="user-count-badge">{noOfUser}</span>
            </div>
            <p className="header-subtitle">
              Track and manage user consents and agreements
            </p>
          </div>

          <div className="search-section">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search by email..."
                value={searchMail}
                onChange={(e) => setSearchMail(e.target.value)}
                onKeyPress={handleKeyPress}
                className="search-input"
              />
              <button className="consent-search-btn" onClick={handleSearch}>
                Search
              </button>
            </div>
          </div>
        </div>

        <div className="content-card">
          <div className="table-header">
            <div className="table-info">
              <Filter size={18} />
              <span>
                Showing {data.length} of {noOfUser} records
              </span>
            </div>
          </div>

          <Table
            data={data}
            page={page}
            setPage={setPage}
            noOfUser={noOfUser}
            limit={limit}
            adminType={adminType}
          />
        </div>
      </div>

      {loader && <Loader />}
    </>
  );
}

// Table component remains the same as before
const Table = ({ data, page, setPage, noOfUser, limit, adminType }) => {
  const handleChange = (event, value) => {
    setPage(value);
  };

  const formatDate = (date) => {
    let dateTemp = moment(date).format("MMMM Do YYYY, h:mm:ss a");
    return dateTemp;
  };

  return (
    <>
      <div className="consent-table-container">
        <div className="table-wrapper">
          <table className="consent-table">
            <thead>
              <tr>
                <th>
                  <div className="table-header-cell">
                    <User size={16} />
                    <span>Name</span>
                  </div>
                </th>
                <th>
                  <div className="table-header-cell">
                    <Mail size={16} />
                    <span>Email</span>
                  </div>
                </th>
                <th>
                  <div className="table-header-cell">
                    <FileText size={16} />
                    <span>Terms & Conditions</span>
                  </div>
                </th>
                <th>
                  <div className="table-header-cell">
                    <Signature size={16} />
                    <span>Digital Signature</span>
                  </div>
                </th>
                <th>
                  <div className="table-header-cell">
                    <Globe size={16} />
                    <span>IP Address</span>
                  </div>
                </th>
                <th>
                  <div className="table-header-cell">
                    <Calendar size={16} />
                    <span>Timestamp</span>
                  </div>
                </th>
                <th>
                  <div className="table-header-cell">
                    <span>Action</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.length > 0 ? (
                data.map((item, key) => (
                  <tr key={key} className="consent-row">
                    <td>
                      <div className="data-cell name-cell">
                        <div className="user-avatar">
                          {item?.name?.charAt(0) || "U"}
                        </div>
                        <span className="data-value">{item?.name || "-"}</span>
                      </div>
                    </td>
                    <td>
                      <div className="data-cell">
                        <span className="data-value email-value">
                          {item?.email || "-"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="data-cell">
                        <span
                          className={`status-badge ${
                            item?.termsAccepted
                              ? "status-accepted"
                              : "status-pending"
                          }`}
                        >
                          {item?.termsAccepted ? "Accepted" : "Not Accepted"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="data-cell">
                        <span className="data-value signature-value">
                          {item?.signature || "-"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="data-cell">
                        <span className="data-value ip-value">
                          {item?.ipAddress || "-"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="data-cell">
                        <span className="data-value timestamp">
                          {formatDate(item?.createdAt)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="action-cell">
                        <UserPDF
                          name={item?.name}
                          email={item?.email}
                          termsAccepted={item?.termsAccepted}
                          signature={item?.signature}
                          ipAddress={item?.ipAddress}
                          timestamp={formatDate(item?.createdAt)}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr className="empty-row">
                  <td colSpan="7">
                    <div className="empty-state">
                      <div className="empty-icon">
                        <FileText size={48} />
                      </div>
                      <h3>No Consents Found</h3>
                      <p>
                        {adminType === "sackAdmin755"
                          ? "Search by email to view a user's consent data"
                          : "No user consents are available at the moment"}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {data.length > 0 && (
          <div className="table-footer">
            <div className="pagination-info">
              Showing page {page} of {Math.ceil(noOfUser / limit)}
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-button prev-button"
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
              >
                <ChevronLeft size={18} />
                Previous
              </button>

              <Stack spacing={2}>
                <Pagination
                  count={Math.ceil(noOfUser / limit)}
                  page={page}
                  onChange={handleChange}
                  shape="rounded"
                  color="primary"
                  size="medium"
                />
              </Stack>

              <button
                className="pagination-button next-button"
                onClick={() => setPage(page + 1)}
                disabled={page >= Math.ceil(noOfUser / limit)}
              >
                Next
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      <style jsx="true">{``}</style>
    </>
  );
};

export default UsersConsent;
